'use client';

import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import HeroSection from '../components/HeroSection';
import ExploreSection from '../components/ExploreSection';
import SubjectsSection from '../components/SubjectsSection';
import ContactSection from '../components/ContactSection';
import SignupPage from '../components/SignupPage';
import TutorsPage from '../components/TutorsPage';
import ReviewsPage from '../components/ReviewsPage';
import ShopPage from '../components/ShopPage';
import PricingPage from '../components/PricingPage';
import NewsletterPage from '../components/NewsletterPage';
import OrderConfirmationPage from '../components/OrderConfirmationPage';
import PrivacyPolicyPage from '../components/PrivacyPolicyPage';

const VALID_PAGES = ['home', 'tutors', 'reviews', 'shop', 'pricing', 'signup', 'newsletter', 'order-confirmation', 'contact', 'privacy-policy'];

function getPageFromHash(): string {
  if (typeof window === 'undefined') return 'home';
  const hash = window.location.hash.replace('#', '');
  return VALID_PAGES.includes(hash) ? hash : 'home';
}

export default function Home() {
  const [activePage, setActivePage] = useState('home');
  const [orderDetails, setOrderDetails] = useState<{
    packageLabel: string;
    packageHours: number;
    packagePrice: number;
    customerName: string;
    customerEmail: string;
  } | null>(null);

  // On mount, read the current hash to set initial page
  useEffect(() => {
    setActivePage(getPageFromHash());

    const handleHashChange = () => {
      setActivePage(getPageFromHash());
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleNavigate = (page: string) => {
    const newHash = page === 'home' ? '' : `#${page}`;
    window.location.hash = newHash;
    setActivePage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOrderConfirmed = (details: {
    packageLabel: string;
    packageHours: number;
    packagePrice: number;
    customerName: string;
    customerEmail: string;
  }) => {
    setOrderDetails(details);
    handleNavigate('order-confirmation');
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a1628', color: '#ffffff', position: 'relative' }}>
      <Navbar activePage={activePage} onNavigate={handleNavigate} />

      <main style={{ paddingTop: 64, position: 'relative', zIndex: 1 }}>
        {/* HOME PAGE */}
        {activePage === 'home' && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            <HeroSection onNavigate={handleNavigate} />
            <ExploreSection onNavigate={handleNavigate} />
            <SubjectsSection />
            <ContactSection />
          </div>
        )}

        {/* TUTORS PAGE */}
        {activePage === 'tutors' && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            <TutorsPage />
          </div>
        )}

        {/* REVIEWS PAGE */}
        {activePage === 'reviews' && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            <ReviewsPage />
          </div>
        )}

        {/* SHOP PAGE */}
        {activePage === 'shop' && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            <ShopPage />
          </div>
        )}

        {/* PRICING PAGE */}
        {activePage === 'pricing' && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            <PricingPage onOrderConfirmed={handleOrderConfirmed} />
          </div>
        )}

        {/* NEWSLETTER PAGE */}
        {activePage === 'newsletter' && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            <NewsletterPage />
          </div>
        )}

        {/* SIGN UP PAGE */}
        {activePage === 'signup' && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            <SignupPage onNavigate={handleNavigate} />
          </div>
        )}

        {/* ORDER CONFIRMATION PAGE */}
        {activePage === 'order-confirmation' && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            <OrderConfirmationPage orderDetails={orderDetails} onNavigate={handleNavigate} />
          </div>
        )}

        {/* CONTACT PAGE */}
        {activePage === 'contact' && (
          <div style={{ animation: 'fadeIn 0.4s ease', paddingTop: '2rem' }}>
            <ContactSection />
          </div>
        )}

        {/* PRIVACY POLICY PAGE */}
        {activePage === 'privacy-policy' && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            <PrivacyPolicyPage onNavigate={handleNavigate} />
          </div>
        )}
      </main>

      {/* Footer */}
      <footer
        className="site-footer"
        style={{
          borderTop: '1px solid rgba(255,255,255,0.06)',
          padding: '2.5rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '0.75rem',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.1rem',
            color: '#f4c430',
            fontWeight: 700,
          }}
        >
          Stellar<span style={{ color: '#ffffff' }}>Scholar</span>
        </div>
        <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.35)', margin: 0 }}>
          &copy; {new Date().getFullYear()} Stellar Scholar Tutoring Services. All rights reserved.
        </p>
        <button
          onClick={() => handleNavigate('privacy-policy')}
          style={{
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.35)',
            fontSize: '0.82rem',
            cursor: 'pointer',
            padding: 0,
            textDecoration: 'underline',
            textUnderlineOffset: 3,
          }}
        >
          Privacy Policy
        </button>
      </footer>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
